import { z } from 'zod';
import { ChatMessageEnvelopeSchema, ChatStreamMetadataSchema, ClientChatMessageIdSchema } from '../../shared/chat-message';
import { ChatAttachmentSchema, ChatUploadPresignRequestSchema, ChatUploadPresignResponseSchema, APP_CHAT_UPLOAD_KEY_REGEX, MAX_CHAT_ATTACHMENTS_PER_MESSAGE } from '../../shared/chat-attachment';
import { GenerateDocumentOutputSchema } from '../download-omniscient-export/download-omniscient-export.schema';
import { BaseErrorSchema } from '../../shared/error.schema';
import { AiProvenanceSchema } from '../../shared/ai-provenance';
/**
 * App-chat bridge schemas.
 *
 * A chat kind is declared in the app manifest (`chat` section, see
 * `AppChatDeclSchema` in the app-registry feature) and defined in the app
 * package via the app-sdk's `defineChat`. The definition itself — Zod schemas,
 * tool handlers, the initializer — is a bundle of functions and never crosses
 * the worker boundary. What crosses is the **descriptor** below: the
 * serializable projection the backend needs to build the agent loop.
 */ /**
 * The persisted part types a tool may declare its output as. A CLOSED list, and
 * a subset of the closed part union — an app can only produce a part the
 * platform already knows how to render, so declaring one adds no renderer and
 * no contract surface. `satisfies` pins it to the union's own keys: renaming a
 * part type there breaks the build here rather than silently orphaning the
 * declaration.
 */ export const APP_CHAT_RICH_PART_TYPES = [
    'tool-renderChart',
    'tool-generateDocument',
    'tool-renderEntityView'
];
export const AppChatRichPartTypeSchema = z.enum(APP_CHAT_RICH_PART_TYPES);
/**
 * What an APP's `generateDocument` reports into the chat turn: a file in the
 * app file store, plus what the model needs to describe it honestly.
 *
 * A second shape beside `GenerateDocumentOutputSchema` rather than a widening
 * of it, because the two name different objects behind different endpoints —
 * the legacy one an Omniscient export URL, this one
 * `/api/v2/apps/:appId/files/:fileId/url`, whose RESPONSE shape also differs
 * (`{url, expiresIn}` against `{downloadUrl, urlExpiresAt}`).
 *
 * Both fold into {@link ChatDocumentLocatorSchema} below.
 *
 * No `expiresAt`: an app file has no declared retention today (the object-store
 * lifecycle for the `apps/` prefix is unverified), and inventing a deadline for
 * the model to quote would be worse than omitting one. The tool description
 * drops core's "say until when the link works" clause for the same reason.
 *
 * Derived from the legacy shape rather than restated: what an export REPORTS —
 * the name, the row count, the two honesty fields and their defaults — is the
 * same question whichever store holds the bytes, and only the locator differs.
 */ export const AppFileDocumentOutputSchema = GenerateDocumentOutputSchema.omit({
    downloadUrl: true,
    expiresAt: true
}).extend({
    variant: z.literal('appFile'),
    appId: z.string().min(1),
    fileId: z.string().min(1)
});
/**
 * What a persisted `generateDocument` part names, in either of the exactly two
 * shapes a transcript can hold: a legacy Omniscient export (migrated history)
 * or a file in the app store (everything generated after the move). The card
 * renders both and the host resolves each through its own endpoint — the two
 * hops answer different shapes, so the branch cannot be pushed down into one.
 *
 * A `z.union` and not a `z.discriminatedUnion`: the legacy shape carries no
 * `variant` key at all, so it is recognised by that ABSENCE. Bolting a defaulted
 * literal onto it purely to discriminate would rewrite what a stored part parses
 * to; a plain union needs no such key.
 */ export const ChatDocumentLocatorSchema = z.union([
    GenerateDocumentOutputSchema,
    AppFileDocumentOutputSchema
]);
/** Whether a locator names an app-store file rather than a legacy export. */ export function isAppFileDocument(document) {
    return 'variant' in document;
}
/**
 * One tool the chat kind exposes to the model. `inputJsonSchema` is the
 * JSON-schema projection of the tool's Zod input schema — it guides the model;
 * the worker re-validates arguments against the real Zod schema before calling
 * the handler (same double-validation as `ai.generateStructured`).
 */ export const AppChatToolDescriptorSchema = z.object({
    /**
     * Addressed by the model and by `invoke-chat-tool`; the character set is the
     * intersection every provider's tool-name rule accepts.
     */ name: z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/, 'Tool names must be 1-64 characters of letters, digits, underscores, or hyphens'),
    description: z.string().min(1),
    inputJsonSchema: z.record(z.string(), z.unknown()),
    /**
     * Persist this tool's settled call as the named platform rich part instead of
     * the generic `tool-app` shape. Absent — the default — keeps the flattening.
     * The host still validates the rewritten part against that part's own schema
     * and falls back to `tool-app` if it does not hold: a declaration is a
     * request, never a bypass of the write gate.
     *
     * The declared type must be the tool's own name prefixed with `tool-`, and the
     * refine below rejects the descriptor otherwise. Not a style rule: the AI SDK
     * streams a call as `tool-<name>`, so a LIVE turn dispatches on the name while
     * a RELOADED one dispatches on this declaration. A tool `exportExcel`
     * declaring `tool-generateDocument` would render as a plain activity row
     * mid-stream and turn into a download card after a refetch — the same call
     * drawn two ways. Failing at `describe-chat` makes the two keys one.
     */ richPart: AppChatRichPartTypeSchema.optional()
}).superRefine((tool, ctx)=>{
    if (tool.richPart && tool.richPart !== `tool-${tool.name}`) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'richPart'
            ],
            message: `A tool declaring "${tool.richPart}" must be named "${tool.richPart.slice('tool-'.length)}"`
        });
    }
});
/**
 * Who a session of a chat kind belongs to.
 *
 * `shared-by-subject` is the original model and stays the default: the session
 * is keyed on the subject, and every viewer who passes the kind's visibility
 * resumes the same transcript.
 *
 * `per-user-private` is the private-chat model: the session belongs to the user
 * whose first turn created it, no subject document exists, and no other user —
 * however entitled — may read, continue or list it. Enforcement is the stored
 * `ownerUserId` predicate in the repository, NOT this declaration: a kind that
 * loses the flag must not turn its users' existing sessions into shared ones.
 */ export const APP_CHAT_SESSION_OWNERSHIP_MODES = [
    'shared-by-subject',
    'per-user-private'
];
export const AppChatSessionOwnershipSchema = z.enum(APP_CHAT_SESSION_OWNERSHIP_MODES);
/**
 * Langfuse prompt keys the kind composes its system prompt from: one base
 * prompt plus the per-case overlay the initializer picks.
 */ export const AppChatPromptKeysSchema = z.object({
    base: z.string().min(1),
    overlays: z.array(z.string().min(1)).default([])
});
/**
 * Everything the backend needs to run a chat kind without loading its bundle:
 * returned by the worker's `describe-chat` operation and validated here at the
 * trust boundary.
 */ export const AppChatDescriptorSchema = z.object({
    kind: z.string().min(1),
    promptKeys: AppChatPromptKeysSchema,
    /**
     * Bundled prompt bodies keyed by prompt key — used when Langfuse is
     * unconfigured or unreachable, mirroring the app's own fallback map.
     */ fallbackPrompts: z.record(z.string(), z.string()),
    tools: z.array(AppChatToolDescriptorSchema),
    /**
     * Whether the bundle exposes `initialize(ctx, subjectKey)`. Kinds without
     * an initializer cannot back the explicit session-start operation.
     */ hasInitializer: z.boolean(),
    /**
     * See {@link AppChatSessionOwnershipSchema}. Absent means
     * `shared-by-subject` — a bundle built before ownership modes existed keeps
     * the model it was written against.
     */ sessionOwnership: AppChatSessionOwnershipSchema.optional(),
    /**
     * Name a session after its first turn, using the platform's shared title
     * generation. Absent — the default — leaves every session untitled, which is
     * what a `shared-by-subject` kind wants: it is named by its subject, not by
     * whatever its first viewer happened to ask.
     */ titles: z.boolean().optional(),
    /**
     * Whether the bundle exposes `starters(ctx, language)` — the app-authored
     * generation behind the empty state's suggested questions. Absent means the
     * kind has none and the starters endpoint answers with an empty list
     * (a bundle built before the hook existed keeps the model it was written
     * against).
     */ hasStarters: z.boolean().optional(),
    /**
     * Whether the bundle exposes `promptVariables(ctx, turn)` — the app-authored
     * per-turn hook behind the `{{placeholder}}` values of its prompt bodies.
     * Absent means the kind has none and no worker run happens per turn (a
     * bundle built before the hook existed keeps the model it was written
     * against).
     */ hasPromptVariables: z.boolean().optional()
}).superRefine((descriptor, ctx)=>{
    // Duplicate names make tool dispatch ambiguous: the agent loop keys tools
    // by name, so the second definition would silently shadow the first.
    const seen = new Set();
    descriptor.tools.forEach((tool, index)=>{
        if (seen.has(tool.name)) {
            ctx.addIssue({
                code: 'custom',
                path: [
                    'tools',
                    index,
                    'name'
                ],
                message: `Duplicate tool name '${tool.name}'`
            });
        }
        seen.add(tool.name);
    });
    // A title names ONE user's chat in their own sidebar. A shared-by-subject
    // session is named by its subject and has no list surface to be found in,
    // so titling it would spend a model call on a string nothing renders — and
    // would name a shared transcript after whatever its first viewer asked.
    if (descriptor.titles && descriptor.sessionOwnership !== 'per-user-private') {
        ctx.addIssue({
            code: 'custom',
            path: [
                'titles'
            ],
            message: 'Only a per-user-private chat kind can declare titles — a shared-by-subject session is named by its subject'
        });
    }
    // Without a bundled base body, every turn dies whenever Langfuse is
    // unreachable — the fallback is the whole point of shipping bodies.
    const baseFallback = descriptor.fallbackPrompts[descriptor.promptKeys.base];
    if (typeof baseFallback !== 'string' || baseFallback.trim() === '') {
        ctx.addIssue({
            code: 'custom',
            path: [
                'fallbackPrompts',
                descriptor.promptKeys.base
            ],
            message: `Missing fallback body for base prompt key '${descriptor.promptKeys.base}'`
        });
    }
});
/**
 * Suggested empty-state questions for one user of one chat kind, in their
 * language.
 *
 * ONE schema for two boundaries, because it is the same payload at both: what
 * the app's `starters` hook returns across the worker boundary, and what the
 * starters endpoint answers with. `aiProvenance` is the app's own mark for the
 * generation (plan 022) — absent when nothing was generated, which is also what
 * a failed or timed-out hook fails closed to (an empty list, never an error).
 */ export const AppChatStartersSchema = z.object({
    starters: z.array(z.string()),
    aiProvenance: AiProvenanceSchema.optional()
});
/**
 * Cap on one `promptVariables` answer, measured across ALL its values together
 * — which is the thing the cost is actually a function of. The values land
 * inside the system prompt of every turn, so an unbounded record is an
 * unbounded per-turn token bill and, past the context window, a turn that
 * cannot run at all. Per-value and per-key caps would not bound that: twenty
 * separately-legal values still concatenate into one prompt.
 *
 * Generous enough that no honest hook meets it — roughly 4k tokens, against a
 * measured consumer of a few hundred characters. A violation is therefore a
 * bug, and it fails the parse rather than being silently truncated (a
 * half-supplied context reads to the model as a complete one).
 */ export const APP_CHAT_PROMPT_VARIABLES_TOTAL_MAX = 16_000;
/**
 * Prompt variables the app supplies for ONE turn — the `{{placeholder}}` values
 * its own prompt bodies are written against, computed against live tenant data
 * instead of round-tripped through a zero-argument tool call.
 *
 * Strings only: the value is substituted into prompt text verbatim, so anything
 * else would only mean the app and the platform disagreeing about how it
 * stringifies.
 *
 * The host's `user_language` is authoritative and OVERWRITES a key of that name
 * — see `AppChatAgentFactory.create`.
 */ export const AppChatPromptVariablesSchema = z.record(z.string().min(1), z.string()).refine((variables)=>Object.values(variables).reduce((total, value)=>total + value.length, 0) <= APP_CHAT_PROMPT_VARIABLES_TOTAL_MAX, `A chat kind may supply at most ${APP_CHAT_PROMPT_VARIABLES_TOTAL_MAX} characters of prompt variables per turn`);
/**
 * Result of the app-side initializer: the subject document the session hangs
 * off (app storage id, opaque to the platform) and the overlay prompt key
 * chosen for this case. Computed once at session start and stored on the
 * session — the backend never re-runs the resolver per turn.
 */ export const AppChatInitializeResultSchema = z.object({
    subjectRef: z.string().min(1),
    overlayPromptKey: z.string().min(1).optional()
});
/**
 * What the HOST knows about the turn a tool call was made in, handed to every
 * tool handler as `ctx.turnEvidence`.
 *
 * The point is that it is host-derived, never model-derived: a guardrail that
 * refuses to write a value "unless the user provided it" (PRD §4.2) can only be
 * enforced against something the model cannot forge, so the current user
 * message's text and the storage keys of the documents it attached are passed
 * down verbatim from the request.
 */ export const AppChatTurnEvidenceSchema = z.object({
    /** The current user message's text, exactly as it was sent. */ text: z.string(),
    /**
   * The turn key of the current user message (`ChatWithAppRequest.message.id`).
   *
   * Every tool call the model makes while answering one message sees the same
   * value, which is what lets an app group the writes of one turn — the
   * planner's change log stamps it on each entry so the undo strip can show
   * "what this answer changed". Optional because a kind may be invoked outside
   * a user turn (an initializer's own writes belong to no turn).
   */ turnId: z.string().optional(),
    /**
   * The subject the session is about — the same value `start` returned and the
   * session stores.
   *
   * Load-bearing, not informational: every other identifier a tool handler sees
   * is a MODEL-supplied argument, so an app whose tools address a document by id
   * cannot otherwise tell "the document this session is about" from "a document
   * whose id ended up in the context". An injected instruction, or a user who
   * knows a second document's id, would otherwise reach it and write it under
   * this session's audit trail. Optional because a kind may run outside a bound
   * turn; a tool that needs the binding must refuse when it is absent.
   */ subjectRef: z.string().optional(),
    /**
   * Storage keys of the documents the model is READING ON THIS TURN — never the
   * ones it merely referenced. A key that failed any re-check, or whose object
   * the retention sweep has taken, is absent: a guardrail must not be told a
   * document is in front of the model when it is not.
   *
   * It is deliberately NOT "what the user attached in this message". A document
   * attached once is re-sent to the model on every later turn of the session it
   * is still inside (the replay window), and the follow-up questions about it
   * are the normal case — so a value the user gave in turn 1 is still
   * user-provided in turn 3, and this list keeps saying so.
   */ attachmentKeys: z.array(z.string().regex(APP_CHAT_UPLOAD_KEY_REGEX, 'Invalid app-chat upload key')).default([]),
    /**
   * The language the answer is written in, resolved host-side from the user —
   * the same value the prompt's `user_language` carries.
   *
   * Here because a tool can PRODUCE user-facing text that never passes through
   * the model: an exported workbook's sheet names and column headers are read
   * by a person, not by a prompt, so the tool building one needs the language
   * the rest of the turn already agreed on. Optional because a kind may run
   * outside a user turn; a tool that needs it falls back to its own default.
   */ language: z.string().optional()
});
/**
 * A document an app-chat turn asks the model to read.
 *
 * The shared attachment claim, narrowed to the keys the APP-CHAT presign
 * endpoint mints: a legacy owner-scoped key is a well-formed chat upload of the
 * same user and is refused here anyway, because an upload is entitled by the
 * surface it was minted under (see `APP_CHAT_UPLOAD_SCOPE`).
 */ export const AppChatAttachmentSchema = ChatAttachmentSchema.extend({
    key: z.string().regex(APP_CHAT_UPLOAD_KEY_REGEX, 'Invalid app-chat upload key')
});
/**
 * What the client declares before it may upload an app-chat attachment — the
 * shared chat-upload presign request, gated by the app-chat guard matrix.
 */ export const PresignAppChatUploadRequestSchema = ChatUploadPresignRequestSchema;
/**
 * The shared presign envelope (see {@link ChatUploadPresignResponseSchema}),
 * narrowed to the key shape THIS surface mints.
 *
 * The key is echoed rather than derived client-side: it is minted from the
 * caller's verified claims AND the app whose gate they just passed, so the
 * client never chooses where its bytes land or which surface may spend them.
 */ export const PresignAppChatUploadResponseSchema = ChatUploadPresignResponseSchema.extend({
    key: z.string().regex(APP_CHAT_UPLOAD_KEY_REGEX, 'Invalid app-chat upload key')
});
/**
 * Opens (or resumes) the shared session for one subject.
 *
 * `subjectKey` is OPAQUE to the platform — the app's initializer decides what
 * it addresses (the planner passes a recommendation's stable id). Sessions are
 * keyed on `(appId, kind, subjectKey)` and shared by every viewer who passes the
 * kind's visibility, so a second start converges on the first one's session
 * instead of forking a private copy.
 */ export const StartAppChatRequestSchema = z.object({
    subjectKey: z.string().min(1).max(256)
});
export const StartAppChatResponseSchema = z.object({
    sessionId: z.string(),
    /** App-storage id of the subject document the initializer created or found. */ subjectRef: z.string(),
    /** Per-case overlay prompt key the initializer chose, if any. */ overlayPromptKey: z.string().optional(),
    /** False when the session already existed and this call only resumed it. */ created: z.boolean()
});
/**
 * One chat turn. Single-message pattern (`chat-with-bliksy.schema.ts`): the
 * client sends only the new message and the backend replays history from the
 * session.
 *
 * `sessionId` is required for a `shared-by-subject` kind — its session is
 * created by the explicit `start` operation, because it hangs off a subject
 * document only the app can create. It is OPTIONAL for a `per-user-private`
 * kind, whose session has no subject and is minted by the first turn sent
 * without one; the minted id reaches the client on the stream's finish
 * metadata ({@link AppChatStreamMetadataSchema}), never before the turn is
 * stored. Which of the two a kind is is not knowable here, so the omission is
 * refused server-side for a shared kind.
 *
 * `message.id` is the TURN KEY: re-sending an id the session already holds
 * REPLACES that turn in place (retry / regenerate / a lost response), it never
 * appends a second copy. See `replaceTurn`.
 */ export const ChatWithAppRequestSchema = z.object({
    sessionId: z.string().regex(/^[a-f\d]{24}$/i, 'Invalid ObjectId format').optional(),
    message: z.object({
        id: ClientChatMessageIdSchema,
        content: z.string().min(1)
    }),
    /**
   * Documents the turn asks the model to read, referenced by the storage keys
   * the app-chat presign endpoint minted. Shape-validated here; the backend
   * still re-derives ownership from the caller's claims and re-inspects the
   * bytes — see `AppChatAttachmentSchema`.
   */ attachments: z.array(AppChatAttachmentSchema).max(MAX_CHAT_ATTACHMENTS_PER_MESSAGE).optional()
});
/**
 * A stored app-chat message as the history endpoint returns it: the shared
 * envelope plus `content`, the canonical text projection of `parts` (an
 * expand/contract dual write).
 */ export const AppChatUIMessageSchema = ChatMessageEnvelopeSchema.extend({
    content: z.string()
});
export const LoadAppChatHistoryResponseSchema = z.object({
    messages: z.array(AppChatUIMessageSchema),
    /**
   * The subject the session is about, echoed so a reload needs one call.
   * Absent for a `per-user-private` session: it has no subject document.
   */ subjectRef: z.string().optional()
});
/**
 * Message metadata the app-chat SSE stream publishes.
 *
 * `sessionId` names a session the turn BROUGHT INTO EXISTENCE — a
 * `per-user-private` kind's first turn sent without one. Published after the
 * write, never on `start`: a client told the id up front would navigate to a
 * session that a failed turn never created, whose history 404s and whose every
 * later turn is refused.
 */ export const AppChatStreamMetadataSchema = ChatStreamMetadataSchema.extend({
    sessionId: z.string().optional()
});
/**
 * One row of a `per-user-private` kind's session list — the caller's own
 * sessions, most recently updated first.
 *
 * `titleAiProvenance` marks the TITLE, not the session: a title is generated
 * from the session's first turn, so the sidebar needs its own AI mark — the
 * mark of the call that wrote it.
 */ export const AppChatSessionSummarySchema = z.object({
    id: z.string(),
    title: z.string().optional(),
    titleAiProvenance: AiProvenanceSchema.optional(),
    // Title generation runs once, on the session's first turn — `createdAt` is
    // what bounds a sidebar poll waiting for the generated title to replace the
    // seeded fallback. `updatedAt` cannot: it bumps on every message, so a
    // provenance-less row would re-arm the poll forever. Optional: a row may
    // predate timestamps.
    createdAt: z.string().optional(),
    updatedAt: z.string()
});
export const ListAppChatSessionsResponseSchema = z.object({
    sessions: z.array(AppChatSessionSummarySchema)
});
export const AppChatErrorSchema = BaseErrorSchema;

//# sourceMappingURL=app-chat.schema.js.map